import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Eye, EyeOff } from "lucide-react";

const REMEMBER_KEY = "percasa_remember_username";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setUsername(saved);
        setRememberMe(true);
      }
    } catch (_) {}
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username, password, { rememberMe });
      try {
        if (rememberMe) localStorage.setItem(REMEMBER_KEY, username.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      } catch (_) {}
      if (user.status === "pending") {
        navigate("/");
      } else {
        navigate("/");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-panel-left">
        <div className="auth-brand">
          <BrandLogo variant="auth" />
          <div className="auth-brand-desc">
            A modern platform to manage your residence - payments, houses, residents, and more, all in one place.
          </div>
        </div>
      </div>

      <div className="auth-panel-right">
        <div className="auth-brand auth-brand--form" data-testid="login-brand" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 10px" }}>
          <BrandLogo variant="auth" />
        </div>
        <div className="auth-form-title">Welcome back</div>
        <div className="auth-form-sub">Sign in to your PERCASA account</div>

        {error && <div className="auth-error" data-testid="login-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label" htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              className="auth-input"
              placeholder="e.g. 01-1-1 (house-street) or email"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              data-testid="login-username-input"
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="password">Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="password"
                type={showPw ? "text" : "password"}
                className="auth-input"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{ paddingRight: 38, width: "100%" }}
                data-testid="login-password-input"
              />
              <button type="button" onClick={() => setShowPw(s => !s)} title={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text2)", padding: 0, display: "flex" }}
                data-testid="login-toggle-password">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ accentColor: "var(--brand-dark)" }}
                  data-testid="remember-me-checkbox"
                />
                Remember me
              </label>
              <Link to="/forgot-password" style={{ fontSize: 11, color: "var(--brand-dark)", textDecoration: "none", fontWeight: 500 }} data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>

          <button type="submit" className="auth-submit" disabled={loading} data-testid="login-submit-btn">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="auth-link">
          New resident?{" "}
          <Link to="/register" data-testid="register-link">First time login here</Link>
        </div>

      </div>
    </div>
  );
}
